import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Member } from '../../actions/getMembers';
import { sendAdminRequest } from '../../api/websocketApi';
import SearchUsersForm from '../../components/admin/SearchUsersForm';
import EditMemberForm from '../../components/EditMemberForm';
import useStore from '../../store/store';
import MembersPage from '../MembersPage';

interface APIResponse {
    success: boolean;
    errCode?: 'invalid_token' | 'auth_failed' | 'access_denied';
    errDetails?: string;
    members?: Member[];
}

const UserPage = () => {
    const storeUserId = useStore((state) => state.userId);
    const { userId } = useParams();
    const parsedUserId = userId ? Number.parseInt(userId, 10) : undefined;
    const activeUserId = parsedUserId || storeUserId;
    const setTitle = useStore((state) => state.setTitle);
    const [members, setMembers] = useState<Member[]>([]);
    const setFormError = useStore((state) => state.setFormError);
    const [editMemberDialogOpen, setEditMemberDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState<Member | undefined>(
        undefined
    );
    const [memberUpdated, setMemberUpdated] = useState(false);
    const [isAddMode, setIsAddMode] = useState(false);

    const handleMemberDialogClose = () => {
        setEditMemberDialogOpen(false);
        setIsAddMode(false);
        setSelectedMember(undefined);
    };

    const handleEditMember = (member: Member) => {
        setSelectedMember(member);
        setIsAddMode(false);
        setEditMemberDialogOpen(true);
    };

    const handleMemberUpdated = () => {
        setMemberUpdated(true);
    };

    useEffect(() => {
        setTitle('Admin - Users');
    }, [setTitle]);

    useEffect(() => {
        if (activeUserId) {
            sendAdminRequest(
                {
                    endpoint: '/user/get-members',
                    body: {
                        userId: activeUserId,
                    },
                },
                (response: APIResponse) => {
                    const { success, members, errCode, errDetails } = response;
                    if (success && members) {
                        setMembers(members);
                    } else if (errCode) {
                        console.error(errCode);
                        setFormError(errCode);
                        setTimeout(() => setFormError(errCode), 0);
                        if (errDetails) console.error(errDetails);
                    }
                    setMemberUpdated(false);
                }
            );
        } else {
            setMembers([]);
        }
    }, [activeUserId, memberUpdated, setFormError, setMembers]);

    return (
        <>
            <div
                style={{ padding: '40px', margin: 'auto', position: 'sticky' }}
            >
                <SearchUsersForm />
            </div>
            <MembersPage
                members={members}
                isAdmin
                onEditMember={handleEditMember}
                userId={activeUserId}
                onMemberAdded={handleMemberUpdated}
            />
            {editMemberDialogOpen && userId && (
                <EditMemberForm
                    open={editMemberDialogOpen}
                    onClose={handleMemberDialogClose}
                    userId={activeUserId}
                    member={selectedMember}
                    onMemberUpdated={handleMemberUpdated}
                    isAddMode={isAddMode}
                    isAdmin
                />
            )}
        </>
    );
};

export default UserPage;
